import styled from '@emotion/styled';
import {
    faMicrophone,
    faMicrophoneSlash,
    faPhone,
    faVideo,
    faVideoSlash,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OTPublisher, OTSession, OTStreams, OTSubscriber } from 'opentok-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { navigationService } from 'src/config/rootService';
import { hexToRgba } from 'src/shared/helpers/hexToRgba';
import { color, font } from 'src/shared/styles/variables';

const Wrapper = styled.div({
    backgroundColor: color.black,
    height: '100vh',
    width: '100vw',
});

const PublisherContainer = styled.div({
    position: 'absolute',
    width: 300,
    height: 200,
    bottom: 30,
    left: 20,
    borderRadius: 10,
    zIndex: 5,
    boxShadow: '0 8px 20px 0 rgba(15,27,39,0.62)',
    '& div': {
        borderRadius: 10,
        height: '100%',
        width: '100%',
    },
});

const SubscriberContainer = styled.div({
    height: '100vh',
    '& div': {
        height: '100%',
        width: '100%',
    },
});

const IconsContainer = styled.div({
    position: 'absolute',
    display: 'flex',
    bottom: 42,
    left: '50%',
    transform: 'translateX(-50%)',
});

interface IconWrapperProps {
    active?: boolean;
}

const IconWrapper = styled.div<IconWrapperProps>(props => ({
    height: 60,
    width: 60,
    margin: '0 11px',
    backgroundColor: props.active ? hexToRgba(color.pickledBluewood, 0.9) : color.white,
    borderRadius: 30,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
}));

const EndCallIconWrapper = styled(IconWrapper)({
    backgroundColor: color.wildWatermelon,
});

const Icon = styled(FontAwesomeIcon)({
    fontSize: font.size.large,
});

const WaitingText = styled.h3(
    {
        position: 'absolute',
        width: '100%',
        top: '45%',
        left: 0,
        textAlign: 'center',
        color: color.white,
    },
    font.heavy(font.size.header),
);

interface Props {
    apiKey: string;
    sessionId: string;
    sessionToken: string;
    sessionUuid: string;
}

const defaultSessionHelper = {
    disconnect: () => undefined,
};

export const VideoSession: React.FC<Props> = ({ apiKey, sessionId, sessionToken, sessionUuid }) => {
    const { t } = useTranslation();
    const [videoEnabled, setVideoEnabled] = useState(true);
    const [audioEnabled, setAudioEnabled] = useState(true);
    const [sessionHelper, setSessionHelper] = useState(defaultSessionHelper);
    const [waitingForCoach, setWaitingForCoach] = useState(true);

    useEffect(() => {
        return sessionHelper.disconnect;
    }, []);

    const sessionEventHandlers = {
        sessionConnected: (connectionResponse): void => {
            setSessionHelper(connectionResponse.target || defaultSessionHelper);
        },
        sessionDisconnected: () => {
            navigationService.navigate('/rate-session', { session: sessionUuid });
        },
    };

    const subscriberEventHandlers = {
        connected: () => setWaitingForCoach(false),
        disconnected: () => setWaitingForCoach(true),
    };

    const commonProperties = {
        showControls: false,
        width: '100%',
        height: '100%',
        insertMode: 'append' as 'append',
        buttonDisplayMode: 'on' as 'on',
    };

    return (
        <Wrapper>
            <OTSession
                apiKey={apiKey}
                sessionId={sessionId}
                token={sessionToken}
                eventHandlers={sessionEventHandlers}
            >
                <PublisherContainer>
                    <OTPublisher
                        properties={{
                            ...commonProperties,
                            fitMode: 'cover',
                            publishAudio: audioEnabled,
                            publishVideo: videoEnabled,
                        }}
                    />
                </PublisherContainer>

                <OTStreams>
                    <SubscriberContainer>
                        <OTSubscriber
                            eventHandlers={subscriberEventHandlers}
                            properties={{
                                ...commonProperties,
                                fitMode: 'contain',
                            }}
                        />
                    </SubscriberContainer>
                </OTStreams>
            </OTSession>

            {waitingForCoach && (
                <WaitingText>{t('videoSessions:video_session.waiting_for_coach')}</WaitingText>
            )}

            <IconsContainer>
                <IconWrapper onClick={() => setAudioEnabled(!audioEnabled)} active={audioEnabled}>
                    <Icon
                        icon={audioEnabled ? faMicrophone : faMicrophoneSlash}
                        color={audioEnabled ? color.white : color.black}
                    />
                </IconWrapper>

                <EndCallIconWrapper onClick={sessionHelper.disconnect}>
                    <Icon icon={faPhone} color={color.white} />
                </EndCallIconWrapper>

                <IconWrapper onClick={() => setVideoEnabled(!videoEnabled)} active={videoEnabled}>
                    <Icon
                        icon={videoEnabled ? faVideo : faVideoSlash}
                        color={videoEnabled ? color.white : color.black}
                    />
                </IconWrapper>
            </IconsContainer>
        </Wrapper>
    );
};
